import React, { useId } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { bioCounter } from './bioRules';
import { profileBioMessages as b } from './profileBioMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BioFieldProps {
  title: string;
  hint?: string;
  value: string;
  placeholder: string;
  /** The limit of the counter; absent: no counter (the facts). */
  max?: number;
  tall?: boolean;
  /** The text is an AI draft that waits for Keep or Discard. */
  isAiDraft?: boolean;
  /** The sentence under the field when Save is refused, or null. */
  error?: string | null;
  testId: string;
  onChange: (text: string) => void;
  onKeep?: () => void;
  onDiscard?: () => void;
  onCopy?: () => void;
  copied?: boolean;
}

/** One field of the bio tool (design c1): the head with the counter, the text, the AI draft row, Copy. */
const BioFieldComponent: React.FC<BioFieldProps> = (props) => {
  const { title, hint, value, placeholder, max, tall = false, isAiDraft = false, error = null, testId } = props;
  const { onChange, onKeep, onDiscard, onCopy, copied = false } = props;
  const intl = useIntl();
  const id = useId();
  const counter = max === undefined ? null : bioCounter(value, max);
  return (
    <div className={styles.bioField}>
      <div className={styles.bioHead}>
        <label htmlFor={id} className="m-0">
          <b>{title}</b>
        </label>
        {hint !== undefined && <small>{hint}</small>}
        {counter !== null && (
          <span className={clsx(styles.count, counter.over > 0 && styles.countOver)} data-testid={`${testId}-count`}>
            {intl.formatMessage(b.counter, { count: counter.count, max: counter.max })}
          </span>
        )}
      </div>
      <textarea
        id={id}
        className={clsx(styles.text, tall && styles.textTall, isAiDraft && styles.textDraft)}
        value={value}
        placeholder={placeholder}
        data-testid={testId}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      {error !== null && (
        <div className={styles.error} role="alert" data-testid={`${testId}-error`}>
          {error}
        </div>
      )}
      {(isAiDraft || onCopy !== undefined) && (
        <div className={styles.row}>
          {isAiDraft && (
            <>
              <span className={styles.inProfile}>{intl.formatMessage(b.aiDraft)}</span>
              <SMLButton className={styles.sm} onClick={onKeep}>
                {intl.formatMessage(b.keep)}
              </SMLButton>
              <SMLButton className={styles.sm} onClick={onDiscard}>
                {intl.formatMessage(b.discard)}
              </SMLButton>
            </>
          )}
          <span className={styles.grow} />
          {onCopy !== undefined && value !== '' && (
            <SMLButton className={styles.sm} onClick={onCopy}>
              {intl.formatMessage(copied ? b.copied : b.copy)}
            </SMLButton>
          )}
        </div>
      )}
    </div>
  );
};

export const BioField = TypedMemo(BioFieldComponent);
